import { Country, LoadAllCountriesUseCase } from '51trips-frontend-core'
import { matchSorter } from 'match-sorter'
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getFlagUrl } from '../../utils/flagUrl'
import CheckboxSelected from '../../assets/icons/checkbox-selected.svg?react'
import CheckboxUnselected from '../../assets/icons/checkbox-unselected.svg?react'
import SearchIcon from '../../assets/icons/search.svg?react'
import { CountryRepository } from '../../repositories/CountryRepository'
import { Button } from '../Button'
import { Dialog } from '../Dialog'
import { Input } from '../Input'

const loadAllCountriesUseCase = new LoadAllCountriesUseCase(new CountryRepository())




interface CountryPickerDialogProps {
  selectedCountriesIDs: string[]
  onSelectedCountriesIDsChanged: (selectedCountriesIDs: string[]) => void
}

export const CountryPickerDialog = forwardRef<
  React.ElementRef<typeof Dialog<string[]>>,
  CountryPickerDialogProps
>(({ selectedCountriesIDs, onSelectedCountriesIDsChanged }, ref) => {
  /// i18n
  const { t } = useTranslation()

  /// Refs
  const dialog = useRef<React.ElementRef<typeof Dialog<string[]>>>(null)

  /// States

  const [selectedCountries, setSelectedCountries] = useState<Set<string>>(
    new Set(selectedCountriesIDs)
  )
  const [searchQuery, setSearchQuery] = useState<string>('')

  /// Effects

  useEffect(() => {
    setSelectedCountries(new Set(selectedCountriesIDs))
  }, [selectedCountriesIDs])

  /// Memos

  const availableCountries = useMemo<Country[]>(() => {
    return loadAllCountriesUseCase.loadAll()
  }, [])

  const filteredCountries = useMemo(() => {
    return matchSorter(availableCountries, searchQuery, { keys: ['title'] })
  }, [availableCountries, searchQuery])

    const sortCountries = () => {

        if(searchQuery === '') {
            if(selectedCountriesIDs.length > 0) {
                return availableCountries.sort((a, b) => {
                    if(selectedCountriesIDs.includes(a.id) && selectedCountriesIDs.includes(b.id)) {
                        return a.title.localeCompare(b.title)
                    } else if(selectedCountriesIDs.includes(a.id)) {
                        return -1
                    } else if(selectedCountriesIDs.includes(b.id)) {
                        return 1
                    } else {
                        return a.title.localeCompare(b.title)
                    }
                })
            } else {
                return availableCountries.sort((a, b) => a.title.localeCompare(b.title))
            }
        } else {
            return filteredCountries.sort((a, b) => a.title.localeCompare(b.title))
        }
    }

  useImperativeHandle(
    ref,
    () => ({
      open: () => {
        setSearchQuery('')
        return dialog.current!.open()
      },
      close: dialog.current!.close
    }),
    []
  )

  const submit = () => {
    dialog.current!.close()
  }

  /// Actions

  const onSectionItemTapped = (itemID: string) => () => {
    const newSelectedCountries = new Set(selectedCountries)
    if (selectedCountries.size >= 10) {
      selectedCountries.has(itemID) && newSelectedCountries.delete(itemID)
    } else {
      selectedCountries.has(itemID)
        ? newSelectedCountries.delete(itemID)
        : newSelectedCountries.add(itemID)
    }
    setSelectedCountries(newSelectedCountries)
    onSelectedCountriesIDsChanged(Array.from(newSelectedCountries))
  }



  /// Render

  return (
    <Dialog
      ref={dialog}
      width="wide"
      title={
        <div className="flex flex-col md:flex-row md:items-center pt-2">
          <h3 className="text-2xl font-semibold">{t('countryTitleDialog', { ns: 'dashboard' })}</h3>

        </div>
      }
      withCloseButton={
        <Button
          variant="primary"
          label={t('done', { ns: 'dashboard' })}
          className="mt-6 mr-8"
          onClick={submit}
        />
      }
      withHeaderDelimiter
    >
      <Input
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder={t('searchCountries', { ns: 'dashboard' })}
        prefixIcon={SearchIcon}
        className="w-full"
      />


      <div className="space-y-2 h-64 overflow-scroll mx-8 px-8 my-8">

              <div className="ml-2 mt-1 mb-2">
                {sortCountries().map((country) => (
                    <div key={country.id} className="w-full items-center">
                      <button
                          onClick={onSectionItemTapped(country.id)}
                          key={country.id}
                          className="w-full py-0.5 hover:underline text-left flex items-center"
                      >
                        <div className="">
                          <img
                              className="w-8 mr-6"
                              src={getFlagUrl(country.id)}
                              alt=""
                          />
                        </div>
                        <div className="w-full align-left">{country.title}</div>
                        {selectedCountries.has(country.id) ? (
                            <CheckboxSelected className="align-right"/>
                        ) : (
                            <CheckboxUnselected/>
                        )}
                      </button>
                    </div>
                ))}

              </div>
      </div>
    </Dialog>
  )
})
